
import { safeToast } from "@/components/ui/use-toast";
import { ServiceQueryResult } from "@/types/serviceQueryTypes";

// Basic error type categorization
export type SimpleErrorType = 
  | "network" 
  | "server"
  | "database"
  | "timeout"
  | "other";

/**
 * Simple error handler that logs errors and returns empty data
 * without using JSX in the toast action which was causing build errors
 */
export const handleServiceError = (
  error: any,
  isOnline: boolean,
  hasToasted: boolean,
  setHasToasted: (value: boolean) => void,
  clearCacheCallback: () => void
): ServiceQueryResult => {
  // Create a unique error ID for tracking
  const errorId = `err-${Date.now().toString(36)}-${Math.floor(Math.random() * 1000)}`;
  
  // Log the error with structured information
  console.error(`Service query error [${errorId}]:`, {
    message: error?.message,
    code: error?.code,
    name: error?.name,
    hint: error?.hint,
    details: error?.details,
    stack: error?.stack?.substring(0, 200) || 'No stack trace'
  });
  
  // Determine simple error type
  let errorType: SimpleErrorType = "other";
  
  if (!isOnline || error?.message?.includes('network') || error?.message?.includes('fetch') || 
      error?.message?.includes('connect') || error?.code === 'NETWORK_ERROR') {
    errorType = "network";
  } else if (
    error?.status >= 500 || 
    error?.statusCode >= 500 ||
    error?.status === 429 ||
    error?.statusCode === 429 ||
    error?.code === 'PGRST200'
  ) {
    errorType = "server";
  } else if (
    error?.code === 'PGRST116' ||
    error?.code === '400' ||
    error?.hint?.includes('column') ||
    error?.message?.includes('database') ||
    error?.message?.includes('relation')
  ) {
    errorType = "database";
  } else if (error?.message?.includes('timeout')) {
    errorType = "timeout";
  }
  
  // Show toast only once and when online (unless it's a network error)
  if ((isOnline || errorType === "network") && !hasToasted) {
    // Get appropriate message
    let errorMessage = "An error occurred while loading services";
    let errorTitle = "Error loading services";
    
    if (errorType === "network") {
      errorMessage = "Please check your internet connection";
      errorTitle = "Connection issue";
    } else if (errorType === "server") {
      errorMessage = "Server error occurred. Please try again later";
      errorTitle = "Server issue";
    } else if (errorType === "database") {
      errorMessage = "Database error occurred. Please try refreshing";
      errorTitle = "Data issue";
    } else if (errorType === "timeout") {
      errorMessage = "Request took too long. Please try again";
      errorTitle = "Connection timeout";
    }
    
    // Show toast using safeToast which handles non-JSX action formats
    safeToast({
      variant: errorType === "network" ? "warning" : "destructive",
      title: errorTitle,
      description: `${errorMessage} [${errorId}]`, // Include error ID for reporting
      action: {
        label: "Retry",
        onClick: clearCacheCallback
      }
    });
    
    setHasToasted(true);
  }
  
  // Return empty data to prevent cascade failures
  return {
    services: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 0
  };
};
